import clsx from 'clsx'

export type ShapeKind = 'roi' | 'circle' | 'point'

export const SHAPE_META: Record<ShapeKind, { label: string; color: string }> = {
  roi: { label: 'ROIs', color: '#F1A33C' },
  circle: { label: 'Circles', color: '#68CE67' },
  point: { label: 'Points', color: '#067AFF' },
}

// Shape-representative icons — the old 2x2 grid dated from when every annotation
// was a bbox (square). Now each kind gets its own glyph: circle, crosshair, square.
function ShapeIcon({ kind, color }: { kind: ShapeKind; color: string }) {
  if (kind === 'circle') {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.8" aria-hidden="true">
        <circle cx="8" cy="8" r="5.6" />
      </svg>
    )
  }
  if (kind === 'roi') {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.8" aria-hidden="true">
        <rect x="2.6" y="2.6" width="10.8" height="10.8" rx="1" />
      </svg>
    )
  }
  // point -> crosshair
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <path d="M8 1.4V14.6M1.4 8H14.6" />
      <circle cx="8" cy="8" r="1.7" fill={color} stroke="none" />
    </svg>
  )
}

// Bbox-semantics categories (media_classes rows with a real boundary: plain boxes
// + circle annotations). Colors mirror rc-app's ground-truth green / rejected red;
// suggestions use the near-black of its "review" state.
export type BoxKind = 'gt' | 'rejection' | 'suggestion'

export const BOX_META: Record<BoxKind, { label: string; color: string }> = {
  gt: { label: 'Ground truth', color: '#37C759' },
  rejection: { label: 'Rejected', color: '#FE3A30' },
  suggestion: { label: 'Suggestions', color: '#1C1C1E' },
}

// Lab-tuned (variant B2t, 2026-09-18): 24px glyphs with a thin 1.2 stroke —
// scaled-up icons keep the line weight the 16px originals had.
function BoxIcon({ kind, color, size = 24, strokeWidth = 1.2 }: { kind: BoxKind; color: string; size?: number; strokeWidth?: number }) {
  if (kind === 'gt') {
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" aria-hidden="true">
        <rect x="2.6" y="2.6" width="10.8" height="10.8" rx="1" />
        <path d="M5.4 8.2l1.8 1.8 3.4-3.8" />
      </svg>
    )
  }
  if (kind === 'rejection') {
    return (
      <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" aria-hidden="true">
        <rect x="2.6" y="2.6" width="10.8" height="10.8" rx="1" />
        <path d="M5.8 5.8l4.4 4.4M10.2 5.8l-4.4 4.4" />
      </svg>
    )
  }
  // suggestion -> dotted box (lab /labs/suggestion-icon, variant E). Drawn on a 24-unit
  // grid; pathLength=20 makes the round dots tile the perimeter exactly — one on each
  // corner, four between — where the old 2.4/2 dasharray tiled unevenly and smudged.
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth * 1.7} strokeLinecap="round" strokeDasharray="0 1" aria-hidden="true">
      <rect x="3.75" y="3.75" width="16.5" height="16.5" pathLength={20} />
    </svg>
  )
}

interface BoxTypeFilterProps {
  visible: Record<BoxKind, boolean>
  counts: Record<BoxKind, number>
  onToggle: (kind: BoxKind) => void
}

/** Toggle buttons for the bbox world: ground truth / rejected / suggestions.
 *  rc-app-style joined segmented group (lab variant B2t): one control, three
 *  lenses — yellow segment = toggled on, icon keeps its category color. */
export function BoxTypeFilter({ visible, counts, onToggle }: BoxTypeFilterProps) {
  const kinds: BoxKind[] = ['gt', 'rejection', 'suggestion']
  return (
    <div className="flex items-center justify-center">
      {/* Yellow-outlined segment group: the outline is always yellow, a segment
          fills opaque yellow when its category is toggled on (user 2026-09-18). */}
      <div
        className="flex overflow-hidden rounded-[10px]"
        style={{ border: '1.5px solid var(--accent-yellow)' }}
      >
        {kinds.map((kind, i) => {
          const active = visible[kind]
          const { label, color } = BOX_META[kind]
          return (
            <button
              key={kind}
              type="button"
              onClick={() => onToggle(kind)}
              title={`${label} (${counts[kind]})`}
              aria-pressed={active}
              aria-label={`${active ? 'Hide' : 'Show'} ${label}`}
              className={clsx(
                'flex h-9 w-[50px] items-center justify-center transition-colors',
                // Off segments stay transparent so the chrome panel shows
                // through in both themes (a white bg glowed grey in dark mode).
                !active && 'hover:bg-[color-mix(in_srgb,var(--text-primary)_8%,transparent)]',
              )}
              style={{
                borderLeft: i > 0 ? '1.5px solid var(--accent-yellow)' : undefined,
                // Same translucent tint as the grid rows' hover — the yellow
                // outline plus tint reads as "on" without going solid.
                backgroundColor: active
                  ? 'color-mix(in srgb, var(--accent-yellow) 28%, transparent)'
                  : undefined,
              }}
            >
              {/* Suggestion glyph follows the theme — the tinted bg stays
                  translucent, so the panel surface decides the contrast.
                  Deselected icons sit at 50% alpha so "on" pops. */}
              <span className={clsx('transition-opacity', !active && 'opacity-50')}>
                <BoxIcon
                  kind={kind}
                  color={kind === 'suggestion' ? 'var(--text-primary)' : color}
                />
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

interface AnnotationTypeFilterProps {
  visible: Record<ShapeKind, boolean>
  counts: Record<ShapeKind, number>
  onToggle: (kind: ShapeKind) => void
}

/** Three colored toggle buttons that show/hide ROIs, circles and points. */
export function AnnotationTypeFilter({ visible, counts, onToggle }: AnnotationTypeFilterProps) {
  const kinds: ShapeKind[] = ['roi', 'circle', 'point']
  return (
    <div className="flex items-center justify-center gap-2">
      {kinds.map((kind) => {
        const active = visible[kind]
        const { label, color } = SHAPE_META[kind]
        return (
          <button
            key={kind}
            type="button"
            onClick={() => onToggle(kind)}
            title={`${label} (${counts[kind]})`}
            aria-pressed={active}
            aria-label={`${active ? 'Hide' : 'Show'} ${label}`}
            className={clsx(
              'flex h-8 w-8 items-center justify-center rounded-md border transition-colors',
              active ? 'border-transparent' : 'border-border-default/50 bg-white/70 hover:bg-white',
            )}
            style={active ? { backgroundColor: color } : undefined}
          >
            <ShapeIcon kind={kind} color={active ? '#ffffff' : color} />
          </button>
        )
      })}
    </div>
  )
}
